"use client"

import { useState, useEffect } from "react"
import { AccordionItem } from "./Accordion"
import { Row, Col } from "./Grid"
import { addBanner, getBanner, updateBanner, deleteBanner, getImagesByBanner } from "../../../lib/api"
import { BannerPreview } from "./Preview"
import { Table } from "./Table"
import { CustomFileInput } from "./CustomFileInput/CustomFileInput"
import "../../../theme/tailwind/banner.css"
import "../../../theme/tailwind/accordion-custom.css"
import { Edit, Trash2 } from "lucide-react"
import { timezoneUtils } from "../../../utils/timezone"
import { devError, devWarn } from "../../../utils/devConsole"

export default function Banner() {
  const [showNewBannerForm, setShowNewBannerForm] = useState(false)
  const [bannerData, setBannerData] = useState({
    Nome: "",
    DataInizio: "",
    DataFine: "",
  })
  const [erroreNuovoBanner, setErroreNuovoBanner] = useState("")
  const [banners, setBanners] = useState([])
  const [editingId, setEditingId] = useState(null)
  const [refreshKey, setRefreshKey] = useState(0)

  const handleNewBanner = (e) => {
    const { name, value } = e.target
    setBannerData((prev) => ({ ...prev, [name]: value }))
  }

  const handleShowNewBannerForm = () => {
    setShowNewBannerForm(true)
    // Scroll verso il titolo Banner dopo un piccolo ritardo per aggiornare il DOM
    setTimeout(() => {
      const titleElement = document.getElementById('banner-title')
      if (titleElement) {
        titleElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 100)
  }

  const undoNewBanner = () => {
    setBannerData({ Nome: "", DataInizio: "", DataFine: "" })
    setErroreNuovoBanner("")
    setShowNewBannerForm(false)
    setEditingId(null)
  }

  const saveNewBanner = async (e) => {
    e.preventDefault()

    if (!bannerData.DataInizio || !bannerData.DataFine) {
      setErroreNuovoBanner("Entrambe le date sono obbligatorie.")
      return
    }

    // Utilizzo del fuso orario italiano per il confronto delle date
    const dataInizio = timezoneUtils.fromUTC(bannerData.DataInizio);
    const dataFine = timezoneUtils.fromUTC(bannerData.DataFine);
    
    if (dataFine.isBefore(dataInizio)) {
      setErroreNuovoBanner("La data di fine non può essere precedente alla data di inizio.")
      return
    }

    try {
      if (editingId) {
        await updateBanner(editingId, bannerData)
      } else {
        await addBanner(bannerData)
      }
      undoNewBanner()
      fetchBanners()
    } catch (error) {
      devError("Error adding/updating banner:", error)
      
      // Gestione degli errori specifici
      if (error.message && error.message.includes("cannot exceed 255 characters")) {
        setErroreNuovoBanner("Il nome non può superare i 255 caratteri.")
      } else if (error.message && error.message.includes("cannot be empty")) {
        setErroreNuovoBanner("Il nome non può essere vuoto.")
      } else {
        setErroreNuovoBanner("Errore durante l'aggiunta/aggiornamento della bannière.")
      }
    }
  }

  const fetchBanners = async () => {
    try {
      const data = await getBanner()

      const bannersWithImages = await Promise.all(
        data.map(async (b) => {
          const images = await getImagesByBanner(b.banner_id)
          return {
            ...b,
            id: b.banner_id,
            hasImage: images.length > 0,
          }
        }),
      )

      setBanners(bannersWithImages)
    } catch (error) {
      devError("Error fetching banners:", error)
    }
  }

  const editBanner = (banner) => {
    setBannerData({
      Nome: banner.Nome || "",
      DataInizio: banner.DataInizio.split("T")[0],
      DataFine: banner.DataFine.split("T")[0],
    })
    setEditingId(banner.banner_id)
  }

  const removeBanner = async (id) => {
    try {
      await deleteBanner(id)
      fetchBanners()
    } catch (error) {
      devError("Errore durante l'eliminazione del banner:", error)
    }
  }

  useEffect(() => {
    // Verificare l'autenticazione prima di caricare i dati
    const token = localStorage.getItem("authToken")
    if (token) {
      fetchBanners()
    } else {
      devWarn("🔒 [BANNER] Utente non autenticato, dati non caricati")
    }
  }, [refreshKey])

  return (
    <div className="container mx-auto px-1 sm:px-4">
      <div className="text-center mb-6" id="banner-title">
        <h2 className="title-section">Banner</h2>
      </div>

      {showNewBannerForm && (
        <div className="add-plan-form my-8" id="nuovo-banner-form">
          <form onSubmit={saveNewBanner} className="flex flex-col items-center">
            <div className="add-plan-title mb-4">
              <h3 className="text-xl sm:text-2xl md:text-3xl text-[#dc3545] text-center mb-6">Nuovo banner</h3>
            </div>

            <input
              className="w-full sm:w-1/2 border border-gray-300 rounded px-3 py-2 text-center mb-6 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-gray-400 placeholder:text-center"
              name="Nome"
              type="text"
              value={bannerData.Nome || ""}
              onChange={handleNewBanner}
              placeholder="Nome banner"
              style={{ textAlign: 'center' }}
            />

            <div className="flex flex-nowrap items-center justify-center gap-0.5 mb-4">
              <span className="add-plan-date-label text-sm">dal</span>
              <input
                className="border border-gray-300 rounded px-2 py-1 text-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-[140px] sm:w-[140px] text-sm placeholder:text-gray-400 placeholder:italic"
                name="DataInizio"
                type="date"
                value={bannerData.DataInizio}
                onChange={handleNewBanner}
                placeholder="gg/mm/aaaa"
                pattern="\\d{4}-\\d{2}-\\d{2}"
              />
              <span className="add-plan-date-label text-sm">al</span>
              <input
                className="border border-gray-300 rounded px-2 py-1 text-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-[140px] sm:w-[140px] text-sm placeholder:text-gray-400 placeholder:italic"
                name="DataFine"
                type="date"
                value={bannerData.DataFine}
                onChange={handleNewBanner}
                placeholder="gg/mm/aaaa"
                pattern="\\d{4}-\\d{2}-\\d{2}"
              />
              <span className="add-plan-date-label text-sm"></span>
            </div>

            {erroreNuovoBanner && <div className="form-error mb-4 text-center">{erroreNuovoBanner}</div>}

            <div className="add-plan-actions flex gap-2 justify-center mt-4">
              <button type="button" className="px-5 py-3 bg-[#dc3545] text-white rounded transition-colors duration-300 hover:bg-[#c82333] min-w-[100px] w-full sm:w-auto" onClick={undoNewBanner}>
                Annulla
              </button>
              <button type="submit" className="px-5 py-3 bg-[#dc3545] text-white rounded transition-colors duration-300 hover:bg-[#c82333] min-w-[100px] w-full sm:w-auto">
                Salva
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="mt-6">
        {banners.length > 0 && (
          <Row>
            <Col xl={12} lg={12} md={12} sm={12} xs={12}>
              <div className="space-y-6">
                {banners.map((banner, index) => (
                  <AccordionItem
                    key={banner.id}
                    className="accordion-item"
                    title={(isActive) => !isActive && <h2 className="title-accordion">{banner.Nome || `Banner #${banner.Numero || 1}`}</h2>}
                  >
                    <div className="px-0" id={"TabellaBanner" + banner.id}>
                      {editingId === banner.banner_id ? (
                        <>
                          <h3 className="title-plan text-center">Modifica banner</h3>
                          <form onSubmit={saveNewBanner} className="text-center mb-8 flex flex-col items-center">
                            <input
                              className="w-full sm:w-1/2 border border-gray-300 rounded px-3 py-2 text-center mb-6 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-gray-400 placeholder:text-center"
                              name="Nome"
                              type="text"
                              value={bannerData.Nome || ""}
                              onChange={handleNewBanner}
                              placeholder="Nome banner"
                              style={{ textAlign: 'center' }}
                            />
                            <div className="flex flex-nowrap items-center justify-center gap-0.5 mb-4">
                              <span className="add-plan-date-label text-sm">dal</span>
                              <input
                                className="border border-gray-300 rounded px-2 py-1 text-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-[140px] sm:w-[140px] text-sm placeholder:text-gray-400 placeholder:italic"
                                name="DataInizio"
                                type="date"
                                value={bannerData.DataInizio}
                                onChange={handleNewBanner}
                                placeholder="gg/mm/aaaa"
                                pattern="\\d{4}-\\d{2}-\\d{2}"
                              />
                              <span className="add-plan-date-label text-sm">al</span>
                              <input
                                className="border border-gray-300 rounded px-2 py-1 text-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-[140px] sm:w-[140px] text-sm placeholder:text-gray-400 placeholder:italic"
                                name="DataFine"
                                type="date"
                                value={bannerData.DataFine}
                                onChange={handleNewBanner}
                                placeholder="gg/mm/aaaa"
                                pattern="\\d{4}-\\d{2}-\\d{2}"
                              />
                              <span className="add-plan-date-label text-sm"></span>
                            </div>

                            {erroreNuovoBanner && (
                              <div className="text-red-600 text-sm mt-1 mb-4 text-center">{erroreNuovoBanner}</div>
                            )}

                            <div className="add-plan-actions flex gap-2 justify-center mt-4">
                              <button
                                type="button"
                                className="px-5 py-3 bg-[#dc3545] text-white rounded transition-colors duration-300 hover:bg-[#c82333] min-w-[100px] w-full sm:w-auto"
                                onClick={undoNewBanner}
                              >
                                Annulla
                              </button>
                              <button
                                type="submit"
                                className="px-5 py-3 bg-[#dc3545] text-white rounded transition-colors duration-300 hover:bg-[#c82333] min-w-[100px] w-full sm:w-auto"
                              >
                                Salva
                              </button>
                            </div>
                          </form>
                        </>
                      ) : (
                        <>
                          <h2 className="title-plan text-center" id={"NomeBanner" + banner.Numero}>
                            {banner.Nome || `Banner #${banner.Numero || 1}`}
                          </h2>

                          <p className="banner-dates text-center">
                            (dal {timezoneUtils.formatItalianDateShort(banner.DataInizio)} al{" "}
                            {timezoneUtils.formatItalianDateShort(banner.DataFine)})
                          </p>

                          <div className="banner-buttons">
                            <button
                              className="button-custom mx-1"
                              title="ModificaBanner"
                              type="button"
                              onClick={() => editBanner(banner)}
                            >
                              <Edit className="w-5 h-5" />
                            </button>

                            <button
                              className="button-custom mx-1"
                              title="EliminaBanner"
                              type="button"
                              onClick={() => removeBanner(banner.id)}
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </>
                      )}

                      <BannerPreview numP={banner.banner_id} refreshKey={refreshKey} />
                      <div className="mb-6"></div>

                      <Table
                        numP={banner.banner_id}
                        refreshKey={refreshKey}
                        entityType="banner"
                        onDataUpdated={() => setRefreshKey((prev) => prev + 1)}
                      />

                      <CustomFileInput
                        numP={banner.banner_id}
                        entityType="banner"
                        onImageAdded={() => setRefreshKey((prev) => prev + 1)}
                      />
                    </div>
                  </AccordionItem>
                ))}
              </div>
            </Col>
          </Row>
        )}
      </div>

      {!showNewBannerForm && (
        <div className="text-center mt-8">
          <button className="button-custom w-full py-3 px-6" onClick={handleShowNewBannerForm}>
            Aggiungi banner
          </button>
        </div>
      )}
    </div>
  )
}
